import { PasteRule } from '@tiptap/core';
import Mention from '@tiptap/extension-mention';
import { Fragment, Slice } from '@tiptap/pm/model';
import { Plugin } from '@tiptap/pm/state';
import { SUPPORTED_PLACE_HOLDERS } from '../../../../constants/constants';


const mergeClassNames = (baseClass: string, extraClass?: string): string => {
    if (!extraClass) {
        return baseClass;
    }
    const merged = new Set<string>([baseClass, ...extraClass.split(' ')]);
    return Array.from(merged).join(' ');
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const PLACEHOLDER_PATTERN = new RegExp(`(${SUPPORTED_PLACE_HOLDERS.map(escapeRegExp).join('|')})`, 'g');

export const PlaceholderMention = Mention.extend({
    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-mention') || element.getAttribute('data-id'),
                renderHTML: (attributes) => (attributes['id'] ? { 'data-mention': attributes['id'] } : {}),
            },
            label: {
                default: null,
                parseHTML: (element) => element.textContent || element.getAttribute('data-label'),
            },
        };
    },
    parseHTML() {
        return [
            { tag: 'span.mention[data-mention]' },
            { tag: 'span[data-mention]' },
        ];
    },
    renderHTML({ node, HTMLAttributes }) {
        const label = node.attrs['label'] || node.attrs['id'] || '';
        const baseClass = 'mention';
        const className = mergeClassNames(baseClass, HTMLAttributes['class']);
        const attrs = {
            ...HTMLAttributes,
            class: className,
            'data-mention': node.attrs['id'] || node.attrs['label'] || '',
        };
        return ['span', attrs, label];
    },
    addPasteRules() {
        return [
            new PasteRule({
                find: PLACEHOLDER_PATTERN,
                handler: ({ state, range, match }) => {
                    const id = match[0];
                    if (!SUPPORTED_PLACE_HOLDERS.includes(id)) {
                        return null;
                    }
                    const node = this.type.create({ id, label: id });
                    return state.tr.replaceWith(range.from, range.to, node);
                },
            }),
        ];
    },
    addProseMirrorPlugins() {
        const basePlugins = this.parent?.() ?? [];
        const type = this.type;
        const schema = this.type.schema;

        const replaceMentionsInFragment = (fragment: Fragment): Fragment => {
            const nodes = [] as any[];
            fragment.forEach((node) => {
                if (node.type === type) {
                    nodes.push(node);
                    return;
                }

                if (node.isText) {
                    const text = node.text || '';
                    const matcher = new RegExp(PLACEHOLDER_PATTERN.source, 'g');
                    let lastIndex = 0;
                    let match: RegExpExecArray | null;

                    while ((match = matcher.exec(text)) !== null) {
                        const start = match.index;
                        const end = start + match[0].length;
                        if (start > lastIndex) {
                            nodes.push(schema.text(text.slice(lastIndex, start), node.marks));
                        }

                        const id = match[0];
                        if (SUPPORTED_PLACE_HOLDERS.includes(id)) {
                            nodes.push(type.create({ id, label: id }));
                        } else {
                            nodes.push(schema.text(id, node.marks));
                        }

                        lastIndex = end;
                    }

                    if (lastIndex < text.length) {
                        nodes.push(schema.text(text.slice(lastIndex), node.marks));
                    }
                    return;
                }

                if (node.content && node.content.size) {
                    const nextContent = replaceMentionsInFragment(node.content);
                    nodes.push(node.copy(nextContent));
                    return;
                }

                nodes.push(node);
            });

            return Fragment.fromArray(nodes);
        };

        return [
            ...basePlugins,
            new Plugin({
                props: {
                    transformPasted: (slice) => {
                        const content = replaceMentionsInFragment(slice.content);
                        return new Slice(content, slice.openStart, slice.openEnd);
                    },
                },
            }),
        ];
    },
});
